import { DEFAULT_DARK_COLORS_SETTINGS, DEFAULT_LIGHT_COLORS_SETTINGS } from '@/constants';
import { COLOR_SCHEME_OPTIONS, keyOfWidgetSetting, PricingPlan, TimeChoices } from '@/constants/enum';
import { handleToast } from '@/redux/slice/toast.slice';
import { AppDispatch } from '@/redux/store';
import { IAddress } from '@/types/components/location';
import { IWidgetColor } from '@/types/components/widget';
import { RGBAColor } from '@shopify/polaris';

export const openCrisp = () => {
  try {
    $crisp.push(['do', 'chat:open']);
  } catch (error) {
    console.log(error);
  }
};

export const getSomeDaysAgo = (day: number): Date => {
  const date = new Date();
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
};

export const calculateTimeRange = (
  choice: TimeChoices | undefined,
): {
  startTime: number;
  endTime: number;
  previousPeriodStartTime: number;
  previousPeriodEndTime: number;
} => {
  const today = new Date();
  const now = today.getTime();
  const yesterday = new Date(getSomeDaysAgo(1));
  const pastDay = yesterday.getTime();

  switch (choice) {
    case TimeChoices.LAST7DAYS: {
      const endTime = pastDay;
      const startTime = getSomeDaysAgo(7).getTime();
      const previousEndTime = startTime;
      const previousStartTime = getSomeDaysAgo(14).getTime();
      return { startTime, endTime, previousPeriodStartTime: previousStartTime, previousPeriodEndTime: previousEndTime };
    }

    case TimeChoices.LAST30DAYS: {
      const endTime = pastDay;
      const startTime = getSomeDaysAgo(30).getTime();
      const previousEndTime = startTime;
      const previousStartTime = getSomeDaysAgo(60).getTime();
      return { startTime, endTime, previousPeriodStartTime: previousStartTime, previousPeriodEndTime: previousEndTime };
    }

    default: {
      const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      return {
        startTime: startOfToday.getTime(),
        endTime: now,
        previousPeriodStartTime: startOfToday.getTime(),
        previousPeriodEndTime: now,
      };
    }
  }
};

export const isEmpty = (value: unknown) => {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)
  );
};

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
  return emailRegex.test(email);
};

export const handleScrollToElement = (elementId: string) => {
  const element = document.getElementById(elementId);
  element?.scrollIntoView({
    behavior: 'smooth',
  });
};

export const colorValueToCssVar = (colorValue: RGBAColor) => {
  try {
    return `rgba(${colorValue.red}, ${colorValue.green}, ${colorValue.blue}, ${colorValue.alpha})`;
  } catch (e) {
    console.log('colorValueToCssVar', { colorValue, e });
    return 'rgba(255,255,255,1)';
  }
};

export const validateHexColor = (hexColor: string) => {
  return /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hexColor);
};

export const fontSizeValidator = (value: string) => {
  let onlyNumbers = value.replace(/[^0-9]/g, '');
  let fontSize: number | string = parseInt(onlyNumbers, 10);

  if (isNaN(fontSize) || fontSize < 1) {
    fontSize = '';
  }
  if (Number(fontSize) >= 100) {
    fontSize = 100;
  }
  return fontSize;
};

export const openToast = (dispatch: AppDispatch, message: string = 'Saved successfully') => {
  dispatch(
    handleToast({
      content: message,
      error: false,
      isOpen: true,
      hasAction: true,
    }),
  );
};

export const openErrorToast = (dispatch: AppDispatch, message: string = 'Fail saved') => {
  dispatch(
    handleToast({
      content: message,
      error: true,
      isOpen: true,
      hasAction: true,
    }),
  );
};

export const stringToArrayByComma = (input: string) => {
  if (input) return input.split(',').map((item) => item.trim());
  else return [];
};

export const arrayToStringByComma = (array: number[] | string[]) => {
  if (array) return array.map((item) => item.toString()).join(', ');
  else return '';
};

export const time24h = [
  { id: 1, value: '00:00' },
  { id: 2, value: '00:15' },
  { id: 3, value: '00:30' },
  { id: 4, value: '00:45' },
  { id: 5, value: '01:00' },
  { id: 6, value: '01:15' },
  { id: 7, value: '01:30' },
  { id: 8, value: '01:45' },
  { id: 9, value: '02:00' },
  { id: 10, value: '02:15' },
  { id: 11, value: '02:30' },
  { id: 12, value: '02:45' },
  { id: 13, value: '03:00' },
  { id: 14, value: '03:15' },
  { id: 15, value: '03:30' },
  { id: 16, value: '03:45' },
  { id: 17, value: '04:00' },
  { id: 18, value: '04:15' },
  { id: 19, value: '04:30' },
  { id: 20, value: '04:45' },
  { id: 21, value: '05:00' },
  { id: 22, value: '05:15' },
  { id: 23, value: '05:30' },
  { id: 24, value: '05:45' },
  { id: 25, value: '06:00' },
  { id: 26, value: '06:15' },
  { id: 27, value: '06:30' },
  { id: 28, value: '06:45' },
  { id: 29, value: '07:00' },
  { id: 30, value: '07:15' },
  { id: 31, value: '07:30' },
  { id: 32, value: '07:45' },
  { id: 33, value: '08:00' },
  { id: 34, value: '08:15' },
  { id: 35, value: '08:30' },
  { id: 36, value: '08:45' },
  { id: 37, value: '09:00' },
  { id: 38, value: '09:15' },
  { id: 39, value: '09:30' },
  { id: 40, value: '09:45' },
  { id: 41, value: '10:00' },
  { id: 42, value: '10:15' },
  { id: 43, value: '10:30' },
  { id: 44, value: '10:45' },
  { id: 45, value: '11:00' },
  { id: 46, value: '11:15' },
  { id: 47, value: '11:30' },
  { id: 48, value: '11:45' },
  { id: 49, value: '12:00' },
  { id: 50, value: '12:15' },
  { id: 51, value: '12:30' },
  { id: 52, value: '12:45' },
  { id: 53, value: '13:00' },
  { id: 54, value: '13:15' },
  { id: 55, value: '13:30' },
  { id: 56, value: '13:45' },
  { id: 57, value: '14:00' },
  { id: 58, value: '14:15' },
  { id: 59, value: '14:30' },
  { id: 60, value: '14:45' },
  { id: 61, value: '15:00' },
  { id: 62, value: '15:15' },
  { id: 63, value: '15:30' },
  { id: 64, value: '15:45' },
  { id: 65, value: '16:00' },
  { id: 66, value: '16:15' },
  { id: 67, value: '16:30' },
  { id: 68, value: '16:45' },
  { id: 69, value: '17:00' },
  { id: 70, value: '17:15' },
  { id: 71, value: '17:30' },
  { id: 72, value: '17:45' },
  { id: 73, value: '18:00' },
  { id: 74, value: '18:15' },
  { id: 75, value: '18:30' },
  { id: 76, value: '18:45' },
  { id: 77, value: '19:00' },
  { id: 78, value: '19:15' },
  { id: 79, value: '19:30' },
  { id: 80, value: '19:45' },
  { id: 81, value: '20:00' },
  { id: 82, value: '20:15' },
  { id: 83, value: '20:30' },
  { id: 84, value: '20:45' },
  { id: 85, value: '21:00' },
  { id: 86, value: '21:15' },
  { id: 87, value: '21:30' },
  { id: 88, value: '21:45' },
  { id: 89, value: '22:00' },
  { id: 90, value: '22:15' },
  { id: 91, value: '22:30' },
  { id: 92, value: '22:45' },
  { id: 93, value: '23:00' },
  { id: 94, value: '23:15' },
  { id: 95, value: '23:30' },
  { id: 96, value: '23:45' },
];

export const hexToRgbaObject = (hex: string, alpha = 1) => {
  let cleanHex = hex.replace(/^#/, '');

  if (cleanHex.length === 8) {
    const red = parseInt(cleanHex.slice(0, 2), 16);
    const green = parseInt(cleanHex.slice(2, 4), 16);
    const blue = parseInt(cleanHex.slice(4, 6), 16);
    const alpha = parseInt(cleanHex.slice(6, 8), 16) / 255;

    return { red, green, blue, alpha };
  }

  if (cleanHex.length === 3) {
    cleanHex = cleanHex
      .split('')
      .map((char: string) => char + char)
      .join('');
  }

  const red = parseInt(cleanHex.slice(0, 2), 16);
  const green = parseInt(cleanHex.slice(2, 4), 16);
  const blue = parseInt(cleanHex.slice(4, 6), 16);

  return { red, green, blue, alpha };
};

export const rgbaToHex = (color: RGBAColor): string => {
  const redHex = color.red.toString(16).padStart(2, '0');
  const greenHex = color.green.toString(16).padStart(2, '0');
  const blueHex = color.blue.toString(16).padStart(2, '0');
  const alphaHex = Math.round(color.alpha * 255)
    .toString(16)
    .padStart(2, '0');

  return `#${redHex}${greenHex}${blueHex}${alphaHex}`;
};

export const getColor = (
  colorScheme: COLOR_SCHEME_OPTIONS,
  widgetColors: IWidgetColor,
  section: keyof IWidgetColor,
  colorType:
    | keyof IWidgetColor[keyOfWidgetSetting.CALENDAR]
    | keyof IWidgetColor[keyOfWidgetSetting.HEADER]
    | keyof IWidgetColor[keyOfWidgetSetting.BODY],
) => {
  let colors = widgetColors;
  switch (colorScheme) {
    case COLOR_SCHEME_OPTIONS.LIGHT:
      colors = DEFAULT_LIGHT_COLORS_SETTINGS;
      break;
    case COLOR_SCHEME_OPTIONS.DARK:
      colors = DEFAULT_DARK_COLORS_SETTINGS;
      break;
    case COLOR_SCHEME_OPTIONS.CUSTOM:
      colors = widgetColors;
      break;
  }

  const sectionColors = colors[section] as Record<string, RGBAColor>;
  return rgbaToHex(sectionColors[colorType]);
};

export const PREVIEW_LOCATIONS = [
  {
    value: 'sample-1',
    label: 'Sample - Store 1',
    address: '1 World Trade Center, New York, NY 10007',
    position: 1,
  },
  {
    value: 'sample-2',
    label: 'Sample - Store 2',
    address: '20 Fenchurch St, London EC3M 3BY, United Kingdom',
    position: 2,
  },
  {
    value: 'sample-3',
    label: 'Sample - Store 3',
    address: '1-1 Chiyoda, Tokyo 100-8111, Japan',
    position: 3,
  },
  {
    value: 'sample-4',
    label: 'Sample - Store 4',
    address: '5 Avenue Anatole France, 75007 Paris, France',
    position: 4,
  },
];

export const formatAddress = (address: IAddress) => {
  const { address1, address2, city, province, postalCode, country } = address;

  return [address1, address2, city, province, postalCode, country].filter(Boolean).join(', ');
};

export const isAvailableFeature = (availablePlan: PricingPlan, currentPlan: PricingPlan) => {
  let limitPlan = [PricingPlan.Free, PricingPlan.Essential, PricingPlan.Smart, PricingPlan.Enterprise];
  switch (availablePlan) {
    case PricingPlan.Essential: {
      limitPlan = [PricingPlan.Essential, PricingPlan.Smart, PricingPlan.Enterprise];
      break;
    }
    case PricingPlan.Smart: {
      limitPlan = [PricingPlan.Smart, PricingPlan.Enterprise];
      break;
    }
    case PricingPlan.Enterprise: {
      limitPlan = [PricingPlan.Enterprise];
      break;
    }
    default: {
      limitPlan = [PricingPlan.Free, PricingPlan.Essential, PricingPlan.Smart, PricingPlan.Enterprise];
    }
  }

  return limitPlan.includes(currentPlan);
};

export const pricingLimitLocation = (plan: string) => {
  switch (plan) {
    case PricingPlan.Free:
      return 1;
    case PricingPlan.Essential:
      return 2;
    case PricingPlan.Smart:
      return 5;
    case PricingPlan.Enterprise:
      return 20;
    default:
      return 1;
  }
};

export const isValidCSS = (cssString: string) => {
  const sheet = document.createElement('style');
  document.head.appendChild(sheet);
  const rules = cssString.split('}');
  for (let rule of rules) {
    rule = rule.trim();
    if (rule) {
      try {
        sheet?.sheet?.insertRule(rule + '}', sheet?.sheet?.cssRules.length);
      } catch (error) {
        document.head.removeChild(sheet);
        return false;
      }
    }
  }
  document.head.removeChild(sheet);
  return true;
};

export const handleSendMessageCrisp = (message: string) => {
  try {
    $crisp.push(['do', 'chat:open']);
    $crisp.push(['do', 'message:send', ['text', message]]);
    $crisp.push(['set', 'session:data']);
  } catch (error) {
    console.log('$crisp error', error);
  }
};

export const sanitizeValue = (value: string) => {
  let sanitizedValue = value.replace(/[^0-9.]/g, '');

  if (sanitizedValue.startsWith('.')) {
    sanitizedValue = sanitizedValue.substring(1);
  }

  if (sanitizedValue.startsWith('0') && sanitizedValue.length > 1 && !sanitizedValue.startsWith('0.')) {
    sanitizedValue = sanitizedValue.substring(1);
  }

  const dotCount = sanitizedValue.split('.').length - 1;
  if (dotCount > 1) {
    sanitizedValue = sanitizedValue.slice(0, sanitizedValue.lastIndexOf('.'));
  }

  return sanitizedValue;
};

export const convertIdFromShopify = (id: string): string => {
  const regex = /gid:\/\/shopify\/\w+\/(\d+)/;
  const match = id.match(regex);
  return match ? match[1] : id;
};

export const getDateRangeWithPreviousPeriod = (selectedDates: { start: Date; end?: Date }) => {
  const MS_PER_DAY = 1000 * 60 * 60 * 24;

  const start = new Date(selectedDates.start);
  start.setHours(0, 0, 0, 0);
  const end = new Date(selectedDates.end ?? selectedDates.start);
  end.setHours(23, 59, 59, 999);

  const startTime = start.getTime();
  const endTime = end.getTime();

  const diffDays = Math.round((endTime - startTime) / MS_PER_DAY) + 1;

  const previousEndTime = startTime;
  const previousStartTime = previousEndTime - diffDays * MS_PER_DAY;

  return {
    startTime,
    endTime,
    previousPeriodStartTime: previousStartTime,
    previousPeriodEndTime: previousEndTime,
  };
};
